import { processAccountData } from '@Core/API/Data/AccountDataProcessor';
import { processAppData } from '@Core/API/Data/AppDataProcessor';
import { Game } from '@Game/Game';
import { GameStore } from '@GameStore/GameStore';
import { EGameStoreKeys, IGameHistoryState } from '@GameStore/GameStoreSchema';
import { IActionResponseData } from '@GFTypes/Responses/ActionResponse';
import { spinButtonAction, takeButtonAction } from '@Managers/ButtonManager/ButtonActions';
import { EActionName } from '@Managers/Events/EActionName';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { EGameStates } from '@Managers/StateManager/GameStates';

export class GameHistory {
    public static readonly RESPONSE_SPIN_TIME: number = 100;
    public static readonly RESPONSE_TAKE_TIME: number = 1000;
    public static readonly ODOMETER_VISIBILITY_TIME: number = 2000;
    private static readonly SPINS_LIMIT: number = 100;
    private readonly game: Game;
    private readonly gameStore: GameStore;
    private cycleFinished: boolean = true;
    private lastUsedSpinNr: number = 0;

    public gameHistoryData: IGameHistoryState = {
        actionsCollection: [],
        spinsHistory: [],
        spinNr: 0,
        isHistoryMode: false
    };

    constructor(game: Game) {
        this.game = game;
        this.gameStore = this.game.gameStore;
        EventsCenter.on(EGameEvents.INGAME_HISTORY_OPEN, this.openHistory, this);
        EventsCenter.on(EGameEvents.INGAME_HISTORY_CLOSE, this.closeHistory, this);
        EventsCenter.on(EGameEvents.INGAME_HISTORY_REPLAY_SPIN, ({ index }: { index: number }) => this.replaySpin(index));
        EventsCenter.on(EGameEvents.INGAME_HISTORY_INDEX_CHANGE, ({ index }: { index: number }) => this.changeIndex(index));
        EventsCenter.on(EGameStates.POST_WIN_STATE, this.simulateTake, this);
        EventsCenter.on(EGameStates.IDLE_STATE, this.onIdle, this);
    }

    private onIdle(): void {
        this.cycleFinished = true;
    }

    private simulateTake(): void {
        setTimeout(() => {
            if (this.gameStore.get(EGameStoreKeys.GameHistoryState).isHistoryMode) takeButtonAction(this.game);
        }, GameHistory.ODOMETER_VISIBILITY_TIME);
    }

    public replaySpin(spinNr: number): void {
        if (!this.cycleFinished) {
            this.processTakeFromHistory();
            return;
        }
        this.gameHistoryData.spinNr = spinNr;
        this.setBetAndBalanceFromHistory();
        spinButtonAction(this.game);
    }

    private processSpinFromHistory(): void {
        EventsCenter.emit(EGameEvents.ACTION_RESPONSE_RECEIVED, {
            response: this.gameHistoryData.spinsHistory[this.gameHistoryData.spinNr],
            fromHistory: true
        });
        if (this.gameHistoryData.spinsHistory[this.gameHistoryData.spinNr].result.win > 0) {
            this.cycleFinished = false;
        }
        this.lastUsedSpinNr = this.gameHistoryData.spinNr;
    }

    private processTakeFromHistory(): void {
        let spinIndexInActions = this.gameHistoryData.actionsCollection.findIndex(
            (action) => action.cycleId === this.gameHistoryData.spinsHistory[this.lastUsedSpinNr].cycleId
        );
        EventsCenter.emit(EGameEvents.ACTION_RESPONSE_RECEIVED, {
            response: this.gameHistoryData.actionsCollection[spinIndexInActions + 1],
            fromHistory: true
        });
        this.gameStore.set(EGameStoreKeys.IsTakeInitiated, false);
    }

    public startReplay(): void {
        if (this.gameStore.get(EGameStoreKeys.IsTakeInitiated)) {
            this.processTakeFromHistory();
        } else {
            this.processSpinFromHistory();
        }
    }

    private prepareSpinsData(): void {
        const spinsArray = new Map();
        this.gameHistoryData.actionsCollection.forEach((item) => {
            if (!spinsArray.has(item.cycleId)) {
                spinsArray.set(item.cycleId, item);
            }
        });

        this.gameHistoryData.spinsHistory = Array.from(spinsArray.values());
    }

    public saveAction(data: IActionResponseData & { actionName: EActionName }): void {
        const startSymbols = this.gameStore.get(EGameStoreKeys.StartHistorySymbols);
        this.gameHistoryData.actionsCollection.push({
            ...data,
            timeStamp: new Date(),
            betIndex: this.gameStore.get(EGameStoreKeys.BetState).index,
            startSymbols: startSymbols
        });
        this.prepareSpinsData();

        this.gameStore.set(
            EGameStoreKeys.GameHistoryState,
            { actionsCollection: [...this.gameHistoryData.actionsCollection], spinsHistory: [...this.gameHistoryData.spinsHistory] },
            true
        );
        this.checkHistoryLimit();
    }

    private checkHistoryLimit(): void {
        const spinsCount = this.gameHistoryData.spinsHistory.length;
        if (spinsCount > GameHistory.SPINS_LIMIT) {
            const itemsToRemove = spinsCount - GameHistory.SPINS_LIMIT;
            const spinIndexInActions = this.gameHistoryData.actionsCollection.findIndex(
                (action) => action.cycleId === this.gameHistoryData.spinsHistory[itemsToRemove].cycleId
            );
            this.gameHistoryData.spinsHistory.splice(0, itemsToRemove);
            this.gameHistoryData.actionsCollection.splice(0, spinIndexInActions);
        }
    }

    public openHistory(): void {
        this.gameStore.set(EGameStoreKeys.GameHistoryState, { isHistoryMode: true }, true);
        this.changeIndex(0);
    }

    public closeHistory(): void {
        this.gameStore.set(EGameStoreKeys.GameHistoryState, { isHistoryMode: false }, true);
        const lastActionNr = this.gameHistoryData.actionsCollection.length - 1;
        processAppData(this.gameHistoryData.actionsCollection[lastActionNr], this.gameStore);
        this.game.betManager.setIndex(this.gameHistoryData.actionsCollection[lastActionNr].betIndex);
        this.updateVisibleSymbols();
    }

    private changeIndex(index: number): void {
        this.gameHistoryData.spinNr = index;
        this.setBetAndBalanceFromHistory(true);
        this.gameStore.set(EGameStoreKeys.GameHistoryState, { spinNr: this.gameHistoryData.spinNr }, true);
        this.updateVisibleSymbols();
    }

    private setBetAndBalanceFromHistory(baseBet?: boolean): void {
        const clone = structuredClone(this.gameHistoryData.spinsHistory[this.gameHistoryData.spinNr]);
        if (baseBet) clone.account.balance = clone.account.balance + this.game.betManager.getBetValue(clone.betIndex);
        this.gameStore.set(EGameStoreKeys.Account, processAccountData(clone, this.gameStore));
        this.game.betManager.setIndex(clone.betIndex);
    }

    public updateVisibleSymbols(): void {
        const historySpinData = this.gameStore.get(EGameStoreKeys.GameHistoryState);
        let spinNr: number;
        let startSymbols: number[][];
        if (historySpinData.isHistoryMode) {
            spinNr = historySpinData.spinNr;
            startSymbols = historySpinData.spinsHistory[spinNr].startSymbols;
        } else {
            spinNr = historySpinData.spinsHistory.length - 1;
            const outData = historySpinData.spinsHistory[spinNr].result.outcome;
            const outcome = Array.isArray(outData) ? outData[0] : outData;
            startSymbols = outcome.steps[0].finalSymbols.map((reel) => reel.toReversed());
        }
        EventsCenter.emit(EGameEvents.UPDATE_VISIBLE_SYMBOLS, startSymbols);
    }
}
